import { useEffect } from 'react';

import { FormControlLabel } from '@mui/material';
import { useNormalizeReferences } from 'hooks/useNormalizeReferences';
import { ApiClientsAccessSelectInput } from 'pages/ApiClients/components/ApiClientsAccessSelectInput/ApiClientsAccessSelectInput';
import { NewSchemeInfoTooltip } from 'pages/ApiClients/components/NewSchemeInfoTooltip';
import { AccessRole } from 'pages/ApiClients/constants/accessSelectItems';
import { FormDataConsumer, required, useEditController } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { Switch } from 'shared/mui/Switch';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  BooleanInput,
  TextInput,
} from 'shared/react-admin/Inputs';
import { TextInputWithAlias } from 'shared/react-admin/Inputs/TextInputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';

import { ApiClientsEditFormSkeleton } from './ApiClientsEditFormSkeleton';

export type ApiClientEditFormValues = {
  id: string;
  active: boolean;
  name: string;
  alias: string;
  roles: AccessRole[];
  merchant?: string | null;
  merchants?: string[];
};

export const ApiClientsEditForm: React.FC = () => {
  const { ready } = useNormalizeReferences({ referenceFields: ['merchant', 'merchants'] });
  const { setValue, watch, getValues, clearErrors } = useFormContext<ApiClientEditFormValues>();

  const roles = watch('roles');
  const { record } = useEditController();

  useEffect(() => {
    if (roles && roles.includes(AccessRole.Service) && getValues('merchants')?.length) {
      setValue('merchants', []);
    }
  }, [roles]);

  // todo: Вынести в утилиту, так как логика повторяется на форме создания
  const isShowNewAuthScheme = roles?.includes(AccessRole.Merchant);

  if (!ready) {
    return <ApiClientsEditFormSkeleton />;
  }

  return (
    <Box py="24px" width="100%">
      <BooleanInput label="Активен" size="small" source="active" sx={{ marginBottom: '24px' }} />
      <Box display="flex" gap="24px">
        <TextInput
          fullWidth
          label="Название"
          size="small"
          source="name"
          validate={required()}
          variant="outlined"
        />
        <TextInputWithAlias
          fullWidth
          label="Алиас"
          size="small"
          source="alias"
          validate={required()}
          variant="outlined"
        />
        <ApiClientsAccessSelectInput />
      </Box>
      {isShowNewAuthScheme && (
        <>
          <Box marginTop={3}>
            <FormControlLabel
              control={<Switch checked={!record?.merchant} disabled name="scheme" size="small" />}
              label="Новая схема аутентификации"
              sx={{ marginRight: 0, marginLeft: 0 }}
            />
            <NewSchemeInfoTooltip />
          </Box>
          {!record?.merchant ? (
            <ReferenceArrayInput defaultValue={[]} reference="merchants" source="merchants">
              <FormDataConsumer<ApiClientEditFormValues>>
                {({ formData }) => {
                  const disabled = (formData.roles || []).includes(AccessRole.Service);

                  return (
                    <AutocompleteMultiselectInput
                      defaultValue={[]}
                      disabled={disabled}
                      filterToQuery={(searchText: string) => ({ name: searchText })}
                      fullWidth
                      label="Мерчанты"
                      name="merchants"
                      onChange={() => {
                        clearErrors('merchants');
                        clearErrors('merchant');
                      }}
                      optionText="name"
                      size="small"
                      sx={{ marginTop: '24px' }}
                    />
                  );
                }}
              </FormDataConsumer>
            </ReferenceArrayInput>
          ) : (
            <ReferenceInput reference="merchants" source="merchant">
              <AutocompleteInput
                filterToQuery={(searchText: string) => ({ name: searchText })}
                fullWidth
                label="Мерчант"
                optionText="name"
                size="small"
                sx={{ marginTop: '24px' }}
                validate={required()}
                variant="outlined"
              />
            </ReferenceInput>
          )}
        </>
      )}
    </Box>
  );
};
